/**
 * Centralized locale configuration for the application
 * This file contains all supported languages and locale utilities
 */

// Type for layout direction
export type LayoutDirection = "ltr" | "rtl";

/**
 * Language metadata structure
 */
export interface Language {
  code: string; // Short code (en, de, ar)
  locale: string; // Full ISO locale (en-GB, de-CH, ar-AE)
  label: string; // English name (English, German, Arabic)
  display: string; // Native name (English, Deutsch, العربية)
}

/**
 * All supported languages in the application
 * This is the single source of truth for all language-related features
 */
export const SUPPORTED_LANGUAGES: readonly Language[] = [
  { code: "en", locale: "en-GB", label: "English", display: "English" },
  { code: "de", locale: "de-CH", label: "German", display: "Deutsch" },
  { code: "ar", locale: "ar-AE", label: "Arabic", display: "العربية" },
  { code: "fa", locale: "fa-IR", label: "Farsi", display: "فارسی (Persian)" },
  { code: "ps", locale: "ps-AF", label: "Pashto", display: "پښتو (Pashto)" },
  { code: "tr", locale: "tr-TR", label: "Turkish", display: "Türkçe" },
  {
    code: "ku",
    locale: "ku-TR",
    label: "Kurmanji",
    display: "Kurdî (Kurmanji)",
  },
  { code: "ru", locale: "ru-RU", label: "Russian", display: "Русский" },
  { code: "uk", locale: "uk-UA", label: "Ukrainian", display: "Українська" },
  { code: "fr", locale: "fr-CH", label: "French", display: "Français" },
  { code: "es", locale: "es-ES", label: "Spanish", display: "Español" },
] as const;

// RTL (Right-to-Left) languages
// Includes all RTL languages that may be used in the app
// Note: Not all RTL languages are in SUPPORTED_LANGUAGES
export const RTL_LOCALES = ["ar", "fa", "ps", "he", "ur", "yi"] as const;

// Type for full ISO locale codes (en-GB, de-CH, etc.)
export type SupportedLocale = (typeof SUPPORTED_LANGUAGES)[number]["locale"];

// Type for short codes (en, de, ar, etc.)
export type LocaleCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

// Type for RTL locales
export type RtlLocale = (typeof RTL_LOCALES)[number];

/**
 * Helper function to get language by code or locale
 * @param codeOrLocale - Either short code (en) or full locale (en-GB)
 * @returns Language object or undefined
 */
export const getLanguage = (codeOrLocale: string): Language | undefined => {
  return (
    SUPPORTED_LANGUAGES.find(
      (lang) => lang.code === codeOrLocale || lang.locale === codeOrLocale,
    ) || undefined
  );
};

/**
 * Get the short code from a full locale
 * @param locale - Full locale (en-GB, de-CH, etc.)
 * @returns Short code (en, de, etc.)
 */
export const getCodeFromLocale = (locale: string): string => {
  const lang = getLanguage(locale);
  return lang?.code || locale.split("-")[0] || "en";
};

/**
 * Get the full locale from a short code
 * @param code - Short code (en, de, etc.)
 * @returns Full locale (en-GB, de-CH, etc.)
 */
export const getLocaleFromCode = (code: string): string => {
  const lang = getLanguage(code);
  return lang?.locale || "en-GB";
};

/**
 * Check if a locale is a Right-to-Left language
 * Works with both short codes and full locales
 * @param codeOrLocale - The locale code to check (en, ar, en-GB, ar-AE, etc.)
 * @returns true if the locale is RTL, false otherwise
 */
export const isRtlLocale = (codeOrLocale: string): boolean => {
  const code = getCodeFromLocale(codeOrLocale);
  return RTL_LOCALES.includes(code as RtlLocale);
};

/**
 * Validate and sanitize a locale string
 * @param locale - The locale code to validate (short or full)
 * @param fallback - The fallback locale if validation fails (default: "en-GB")
 * @returns A valid full locale code or the fallback
 */
export const validateLocale = (
  locale: string | undefined | null,
  fallback: string = "en-GB",
): string => {
  if (!locale) return fallback;

  const lang = getLanguage(locale);

  if (!lang && process.env.NODE_ENV === "development") {
    console.warn(
      `Invalid locale "${locale}" provided. Falling back to "${fallback}". Supported locales: ${SUPPORTED_LANGUAGES.map((l) => l.locale).join(", ")}`,
    );
  }

  return lang?.locale || fallback;
};

/**
 * Get the text direction for a given locale
 *
 * Accepts any string to handle edge cases and unknown locale codes gracefully.
 * Works with both short codes (en, ar) and full locales (en-GB, ar-AE)
 *
 * @param codeOrLocale - The locale code (can be any string for flexibility)
 * @returns "rtl" for right-to-left languages, "ltr" for left-to-right
 *
 * @example
 * getTextDirection("ar") // "rtl"
 * getTextDirection("ar-AE") // "rtl"
 * getTextDirection("en") // "ltr"
 * getTextDirection("en-GB") // "ltr"
 * getTextDirection("he") // "rtl" (works even though 'he' is not in LANGUAGES)
 * getTextDirection("unknown") // "ltr" (safe fallback)
 */
export const getTextDirection = (codeOrLocale: string): LayoutDirection => {
  return isRtlLocale(codeOrLocale) ? "rtl" : "ltr";
};

/**
 * Get display name for a language (native script)
 * @param codeOrLocale - Short code or full locale
 * @returns Native display name
 */
export const getDisplayName = (codeOrLocale: string): string => {
  const lang = getLanguage(codeOrLocale);
  return lang?.display || codeOrLocale;
};

/**
 * Get label (English name) for a language
 * @param codeOrLocale - Short code or full locale
 * @returns English label
 */
export const getLabel = (codeOrLocale: string): string => {
  const lang = getLanguage(codeOrLocale);
  return lang?.label || codeOrLocale;
};

// Legacy exports for backward compatibility
// These will be gradually phased out in favor of SUPPORTED_LANGUAGES
export const LANGUAGE_NAMES = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((lang) => [lang.code, lang.display]),
);

export const LANGUAGE_NAMES_EN = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((lang) => [lang.code, lang.label]),
);
